import { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
    Megaphone, DollarSign, Users, Sparkles, ArrowLeft, CheckCircle2,
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Public "Become an Ambassador" landing page (route: /ambassadors).
 *
 * Anyone — no login required — can apply with name, desired code,
 * contact, and a short pitch. Goes into the admin Ambassadors panel
 * as a pending application for approval.
 */
const AmbassadorApply = () => {
    const navigate = useNavigate();
    const [form, setForm] = useState({ name: '', code: '', email: '', phone: '', pitch: '' });
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(null);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.code.trim()) {
            toast.error('Please enter your name and a code you want to use.');
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await axios.post(`${API}/ambassadors/apply`, form);
            setSubmitted(data);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Could not submit application');
        }
        setSubmitting(false);
    };

    if (submitted) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#0a0a0a] text-white p-4">
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="max-w-md w-full rounded-2xl p-6 text-center"
                    style={{
                        background: 'linear-gradient(135deg, rgba(168,85,247,0.15), rgba(57,255,20,0.08))',
                        border: '1px solid rgba(168,85,247,0.40)',
                    }}
                    data-testid="ambassador-applied"
                >
                    <CheckCircle2 className="w-14 h-14 mx-auto mb-3 text-[#39FF14]" />
                    <h1 className="text-2xl font-black mb-2">Application received!</h1>
                    <p className="text-[#A0AAB2] text-sm mb-4">
                        Your code <code className="text-[#FFD700] font-mono font-black mx-1">{submitted.code_reserved}</code>
                        is reserved while we review. We'll reach out within 24-48 hours.
                    </p>
                    <Button
                        onClick={() => navigate('/login')}
                        className="font-bold"
                        style={{ background: 'linear-gradient(135deg, #A855F7, #FF4DB8)', color: 'white' }}
                        data-testid="ambassador-applied-home"
                    >
                        Back to BETohalics
                    </Button>
                </motion.div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#0a0a0a] text-white">
            <div className="max-w-3xl mx-auto px-4 py-6">
                <button
                    type="button"
                    onClick={() => navigate(-1)}
                    className="flex items-center gap-1 text-xs text-[#A0AAB2] hover:text-white mb-4"
                    data-testid="ambassador-back"
                >
                    <ArrowLeft className="w-3 h-3" /> Back
                </button>

                {/* Hero */}
                <motion.div
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="rounded-2xl p-6 mb-6 relative overflow-hidden"
                    style={{
                        background: 'linear-gradient(135deg, rgba(168,85,247,0.15), rgba(255,77,184,0.10))',
                        border: '1px solid rgba(168,85,247,0.40)',
                    }}
                >
                    <div className="flex items-center gap-2 mb-2">
                        <Megaphone className="w-5 h-5 text-[#A855F7]" />
                        <span className="text-[10px] font-black uppercase tracking-widest text-[#A855F7]">
                            Become an Ambassador
                        </span>
                    </div>
                    <h1 className="text-3xl sm:text-4xl font-black mb-2">
                        Get paid for every player you bring in.
                    </h1>
                    <p className="text-[#A0AAB2] text-sm sm:text-base max-w-2xl">
                        Share your unique BETohalics code with your network. When they sign up and load credits, we pay you.
                        No cap. Cash App, Venmo, Zelle, PayPal — your choice on payout.
                    </p>
                </motion.div>

                {/* How it works */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
                    <Step
                        icon={Sparkles} color="#FFD700"
                        title="1. Apply"
                        body="Pick the code you want (e.g., MIKE5). It's yours the moment you submit."
                    />
                    <Step
                        icon={Users} color="#39FF14"
                        title="2. Share"
                        body="Hand the code out anywhere — Instagram, TikTok, group chats. New players use it at signup."
                    />
                    <Step
                        icon={DollarSign} color="#A855F7"
                        title="3. Get paid"
                        body="Each player who loads $20+ earns you $5+ (rate set on approval). Cashed out on your schedule."
                    />
                </div>

                {/* Form */}
                <form
                    onSubmit={submit}
                    className="rounded-2xl bg-[#0e0e0e] border border-white/10 p-5 sm:p-6 space-y-4"
                    data-testid="ambassador-apply-form"
                >
                    <h2 className="text-lg font-bold">Apply now</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <Label className="text-[11px] text-[#A0AAB2] mb-1 block">Your name</Label>
                            <Input
                                value={form.name}
                                onChange={(e) => setForm({...form, name: e.target.value})}
                                className="bg-[#0a0a0a] border-white/10 text-white"
                                placeholder="Mike Smith"
                                required
                                data-testid="ambassador-apply-name"
                            />
                        </div>
                        <div>
                            <Label className="text-[11px] text-[#A0AAB2] mb-1 block">Desired code (3-24 chars)</Label>
                            <Input
                                value={form.code}
                                onChange={(e) => setForm({...form, code: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '')})}
                                className="bg-[#0a0a0a] border-white/10 text-white font-mono"
                                placeholder="MIKE5"
                                maxLength={24}
                                required
                                data-testid="ambassador-apply-code"
                            />
                            <p className="text-[10px] text-[#666] mt-0.5">Letters, numbers, underscores only. Reserved instantly.</p>
                        </div>
                        <div>
                            <Label className="text-[11px] text-[#A0AAB2] mb-1 block">Email</Label>
                            <Input
                                type="email"
                                value={form.email}
                                onChange={(e) => setForm({...form, email: e.target.value})}
                                className="bg-[#0a0a0a] border-white/10 text-white"
                                placeholder="you@example.com"
                                data-testid="ambassador-apply-email"
                            />
                        </div>
                        <div>
                            <Label className="text-[11px] text-[#A0AAB2] mb-1 block">Phone</Label>
                            <Input
                                value={form.phone}
                                onChange={(e) => setForm({...form, phone: e.target.value})}
                                className="bg-[#0a0a0a] border-white/10 text-white"
                                placeholder="(555) 555-1234"
                                data-testid="ambassador-apply-phone"
                            />
                        </div>
                    </div>
                    <div>
                        <Label className="text-[11px] text-[#A0AAB2] mb-1 block">Your pitch (optional)</Label>
                        <textarea
                            value={form.pitch}
                            onChange={(e) => setForm({...form, pitch: e.target.value.slice(0, 600)})}
                            rows={4}
                            className="w-full rounded-md bg-[#0a0a0a] border border-white/10 text-white text-sm p-3"
                            placeholder="Tell us about your audience: how big, what platform, why you'd be a great fit. The more context the faster we approve."
                            data-testid="ambassador-apply-pitch"
                        />
                        <p className="text-[10px] text-[#666] mt-0.5">{form.pitch.length}/600</p>
                    </div>
                    <Button
                        type="submit"
                        disabled={submitting}
                        className="w-full font-black text-base h-12"
                        style={{ background: 'linear-gradient(135deg, #A855F7, #FF4DB8)', color: 'white' }}
                        data-testid="ambassador-apply-submit"
                    >
                        {submitting ? 'Submitting…' : 'Submit application'}
                    </Button>
                    <p className="text-[10px] text-[#666] text-center">
                        Submitting doesn't sign you up as a player. Standard payout rate is $5 per qualified signup but rates are set per ambassador.
                    </p>
                </form>
            </div>
        </div>
    );
};

const Step = ({ icon: Icon, color, title, body }) => (
    <div className="rounded-xl bg-[#0e0e0e] border border-white/10 p-4">
        <Icon className="w-6 h-6 mb-2" style={{ color }} />
        <p className="text-white font-bold text-sm">{title}</p>
        <p className="text-xs text-[#A0AAB2] mt-1 leading-relaxed">{body}</p>
    </div>
);

export default AmbassadorApply;
